<script setup lang="ts">
  import {useI18n} from 'vue-i18n';
  import {computed, ref} from 'vue';
  import useCpBem from '~/composables/useCpBem';
  import CpAlert from '~/components/cp/CpAlert/CpAlert.vue';
  import CpSectionHeader from '~/components/cp/CpSection/CpSectionHeader/CpSectionHeader.vue';
  import CpModal from '~/components/cp/CpModal/CpModal.vue';
  import CpIconStandard from '~/components/cp/CpIcon/CpIconStandard/CpIconStandard.vue';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import CpButton from '~/components/cp/CpButton/CpButton.vue';
  import CpRadioButton from '~/components/cp/CpRadioButton/CpRadioButton.vue';

  const props = defineProps<{
    show: boolean
    alreadyReported?: boolean
    success?: boolean
  }>();

  const emit = defineEmits<{
    (e:'update:value', id: string): void
    (e:'update:show', value: boolean): void
    (e:'send-report') : void
    (e:'on-close') : void
  }>();

  const showAlert = ref(false);
  const handleReportButtonClick = () => {
    if (!report.value) {
      showAlert.value = true;
    } else {
      emit('send-report');
    }
  };
  const report = ref('');
  const value = computed({
    get() {
      return props.show;
    },
    set(value) {
      emit('update:show', value);
    },
  });

  const {b, e} = useCpBem('cp-modal-report-ucg');
  const {t} = useI18n();
  const reportOptions = [
    t('commercialContent'),
    t('falseInformation'),
    t('hateLanguage'),
    t('religiousOrPolitical'),
    t('personalInfo'),
    t('illegalConduct'),
  ];
</script>

<template>
    <div :class="b">
        <CpModal
            v-model:show="value"
            persistent
            scroll-strategy="block"
            :fullscreen="!success"
        >
            <CpSectionHeader
                :class="e('title')"
                :title="t('reportContent')"
                type="h3"
                button="close"
                @header-action="value = false; $emit('on-close')"
            />
            <div :class="e`main-content`">
                <div :class="e('content')">
                    <CpIconStandard
                        v-if="success"
                        icon="check_circle"
                        kind="extra-large"
                        type="success"
                    />
                    <CpIconStandard
                        v-else
                        icon="outlined_flag"
                        kind="extra-large"
                        type="primary"
                    />
                    <div v-if="success" :class="e('message')">
                        <div :class="e('subtitle')">
                            <CpText variant="subheading">
                                {{ t('successTitle') }}
                            </CpText>
                        </div>
                        <CpText variant="body-regular">
                            {{ t('successMessage') }}
                        </CpText>
                    </div>
                    <div v-else-if="alreadyReported" :class="e('message')">
                        <div :class="e('subtitle')">
                            <CpText variant="subheading">
                                {{ t('cantReport') }}
                            </CpText>
                        </div>
                        <CpText variant="body-regular">
                            {{ t('errorMessage') }}
                        </CpText>
                    </div>
                    <div v-else :class="e('message')">
                        <div :class="e('subtitle')">
                            <CpText variant="subheading">
                                {{ t('report') }}
                            </CpText>
                        </div>
                        <CpText variant="body-regular">
                            {{ t('anonymousReport') }}
                            <a href="https://www.cyberpuerta.mx/Como-hacer-preguntas-y-respuestas/"><CpText variant="body-link" type="primary" :class="e('link')">{{ t('guidelines') }}</CpText></a>
                            {{ t('selectOption') }}
                        </CpText>
                    </div>
                    <CpAlert
                        v-if="showAlert && !success && !alreadyReported"
                        :class="e('full')"
                        kind="critical"
                        :action="false"
                        :close-icon="false"
                    >
                        <CpText variant="body-regular">
                            {{ t('alertText') }}
                        </CpText>
                    </CpAlert>
                    <div v-if="!success && !alreadyReported" :class="e('options')">
                        <v-radio-group v-model="report" color="primary" @update:model-value="$emit('update:value', $event)">
                            <CpRadioButton
                                v-for="reportType in reportOptions"
                                :key="reportType"
                                :class="e('option-item')"
                                :radio-text="t(reportType)"
                                :selected="report === reportType"
                                :value="reportType"
                            />
                        </v-radio-group>
                    </div>
                </div>
                <div :class="e('buttons')">
                    <CpButton
                        v-if="!success && !alreadyReported"
                        :label="t('reportLabel')"
                        kind="label"
                        color="critical"
                        hollow
                        full-width
                        type="submit"
                        @click="handleReportButtonClick"
                    />
                    <CpButton
                        v-if="!success && !alreadyReported"
                        :label="t('cancel')"
                        kind="label"
                        color="primary"
                        hollow
                        full-width
                        type="button"
                        @click="value = false; $emit('on-close')"
                    />
                    <CpButton
                        v-if="success || alreadyReported"
                        :label="t('back')"
                        kind="label"
                        color="primary"
                        hollow
                        full-width
                        type="button"
                        @click="value = false; $emit('on-close')"
                    />
                </div>
            </div>
        </CpModal>
    </div>
</template>

<style scoped lang="scss">
  @import "assets/style/global.scss";
  .cp-modal-report-ucg {
    &__content {
      display: flex;
      flex-direction: column;
      gap: $cp-gutter;
      padding: $cp-gutter;
      align-items: center;
    }
    &__title {
      border-top: none;
    }
    &__subtitle {
      margin-bottom: $cp-gutter-small;
    }
    &__options {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 0 $cp-gutter;
      align-items: center;

      &:deep(.v-input__details){
        display: none;
      }
      &:deep(.v-label) {
        opacity: unset;
      }
      &:deep(.v-selection-control__input > .v-icon){
        color: var(--color-primary);
        opacity: unset;
      }
      &:deep(.v-selection-control__wrapper) {
        width: unset;
        height: unset;
      }
      &:deep(.v-selection-control__input) {
        height: unset;
        width: unset;
      }
    }
    &__option-item {
      width: 100%;
      margin-top: $cp-gutter-small;
    }
    &__message {
      text-align: center;
      line-height: 16px;
    }
    &__link {
      text-decoration: underline;
      color: var(--color-primary);
    }
    &__full {
      width: 100%;
    }
    &__buttons {
      display: flex;
      flex-direction: column;
      gap: $cp-gutter;
      width: 100%;
      border-top: $cp-border-standard;
      padding: $cp-gutter;
    }
    &__main-content {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
}
</style>
<i18n locale="es-US">
  reportContent: "Reportar contenido"
  report: "Reportar contenido inadecuado "
  anonymousReport: "Puedes reportar anónimamente si consideras que este contenido es inapropiado o no se adhiere a nuestros "
  guidelines: "lineamientos para publicación de preguntas y respuestas. "
  selectOption: "Selecciona el motivo de reporte."
  commercialContent: "Contenido comercial o no deseado"
  falseInformation: "Información falsa"
  hateLanguage: "Lenguaje o símbolos que incitan al odio"
  religiousOrPolitical: "Carácter religioso o político"
  personalInfo: "Inclusión de datos personales que vulneran la integridad"
  illegalConduct: "Promoción de conductas ilícitas o inmorales"
  reportLabel: "Reportar"
  cancel: "Cancelar"
  back: "Regresar"
  alertText: "Favor de indicar el motivo de reporte."
  cantReport: "No es posible reportar este contenido más de una vez "
  errorMessage: "Evaluaremos el reporte que hiciste anteriormente y tomaremos las medidas correspondientes."
  successTitle: "Hemos recibido tu reporte "
  successMessage: "¡Gracias por colaborar en nuestra comunidad! Evaluaremos el contenido reportado y tomaremos las medidas correspondientes."
</i18n>
